import {
  AnalyticsMetric,
  AnalyticsPeriod,
  AppMetricsSnapshot,
  BreakdownDimension,
  MetricBreakdown,
  MetricBreakdownRow,
  MetricPoint,
  MetricTimeSeries,
} from '../../../services/analytics.service';
import { AGGREGATE_APP_IDS, DEMO_PROFILES, DemoAppProfile } from './app-profiles.fixtures';

/**
 * Analytics fixtures.
 *
 * Headline numbers come from app-profiles.fixtures.ts; this file turns them into day-by-day
 * series with weekday seasonality, steady growth, campaign bumps and incidents, so the charts
 * look like real product data (and the story matches reviews, health and AI insights).
 * Everything is deterministic: the same (app, metric) always renders the same chart.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const SERIES_DAYS = 30;

// ------------------------------------------------------------------ deterministic noise

function hashSeed(...parts: string[]): number {
  const str = parts.join('|');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) || 1;
}

/** Mulberry32 PRNG — deterministic, seeded. */
function seededRandom(seed: number): () => number {
  let state = seed;
  return () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const round = (value: number, digits = 0): number => {
  const f = 10 ** digits;
  return Math.round(value * f) / f;
};

// ------------------------------------------------------------------ storyline events

/** Extra crash-rate (percentage points) by days-ago. Acme Shopping v4.12.0 checkout crash, hotfixed in 4.12.1. */
const CRASH_EXCESS: Record<number, Record<number, number>> = {
  8001: { 14: 0.8, 13: 1.7, 12: 2.3, 11: 2.0, 10: 1.3, 9: 0.3 },
};

/** Extra ANR-rate (percentage points) by days-ago. Delivery Partner's foreground-location regression. */
const ANR_EXCESS: Record<number, Record<number, number>> = {
  8003: { 6: 0.25, 5: 0.45, 4: 0.55, 3: 0.3 },
};

/** Multipliers by days-ago applied to users/sessions/new-users for campaigns and incidents. */
const TRAFFIC_EVENTS: Record<number, Record<number, { users?: number; newUsers?: number; sessions?: number; duration?: number }>> = {
  8001: {
    // Autumn Sale campaign
    22: { users: 1.06, newUsers: 1.25, sessions: 1.08 },
    21: { users: 1.12, newUsers: 1.45, sessions: 1.15 },
    20: { users: 1.15, newUsers: 1.5, sessions: 1.2 },
    19: { users: 1.08, newUsers: 1.25, sessions: 1.1 },
    // Checkout crash suppresses sessions and time-in-app
    13: { sessions: 0.97, duration: 0.96 },
    12: { users: 0.97, sessions: 0.93, duration: 0.91 },
    11: { users: 0.97, sessions: 0.93, duration: 0.9 },
    10: { sessions: 0.96, duration: 0.94 },
  },
  8002: {
    // Double-points weekend
    12: { users: 1.1, sessions: 1.16 },
    11: { users: 1.12, sessions: 1.2 },
    5: { users: 1.05, sessions: 1.08 },
  },
};

// ------------------------------------------------------------------ per-app daily series

function stickiness(p: DemoAppProfile): number {
  return Math.min(0.6, 0.12 + (p.sessions / p.mau) * 0.045);
}

function normalizedWeekday(p: DemoAppProfile): number[] {
  const mean = p.weekday.reduce((a, b) => a + b, 0) / 7;
  return p.weekday.map((w) => w / mean);
}

/** Dates (UTC, yyyy-mm-dd) for the trailing 30 days, oldest first, plus days-ago and weekday. */
function seriesDays(): Array<{ date: string; daysAgo: number; weekday: number }> {
  const now = Date.now();
  return Array.from({ length: SERIES_DAYS }, (_, i) => {
    const daysAgo = SERIES_DAYS - 1 - i;
    const d = new Date(now - daysAgo * DAY_MS);
    return { date: d.toISOString().slice(0, 10), daysAgo, weekday: d.getUTCDay() };
  });
}

function buildAppSeries(p: DemoAppProfile, metric: AnalyticsMetric): MetricPoint[] {
  const rand = seededRandom(hashSeed(String(p.id), metric, 'series'));
  const weekdayFactor = normalizedWeekday(p);
  const events = TRAFFIC_EVENTS[p.id] ?? {};
  const growth = p.trends.activeUsers / 100;

  return seriesDays().map(({ date, daysAgo, weekday }, i) => {
    if (daysAgo < p.staleDays) {
      return { date, value: null };
    }

    const wobble = 1 + (rand() - 0.5) * 2 * p.noise;
    const trend = 1 + growth * (i / (SERIES_DAYS - 1) - 0.5);
    const shape = weekdayFactor[weekday] * trend * wobble;
    const ev = events[daysAgo] ?? {};

    switch (metric) {
      case 'ACTIVE_USERS':
        return { date, value: Math.round(p.mau * stickiness(p) * shape * (ev.users ?? 1)) };
      case 'NEW_USERS':
        return { date, value: Math.round((p.newUsers / SERIES_DAYS) * shape * (ev.newUsers ?? ev.users ?? 1)) };
      case 'SESSIONS':
        return { date, value: Math.round((p.sessions / SERIES_DAYS) * shape * (ev.sessions ?? 1)) };
      case 'SESSION_DURATION': {
        const dur = p.avgSessionSeconds * (1 + (wobble - 1) * 0.5) * (1 + growth * 0.25 * (i / SERIES_DAYS - 0.5));
        return { date, value: Math.round(dur * (ev.duration ?? 1)) };
      }
      case 'CRASH_RATE': {
        const excess = CRASH_EXCESS[p.id] ?? {};
        const excessTotal = Object.values(excess).reduce((a, b) => a + b, 0);
        const baseline = Math.max(0.1, 100 - p.crashFreeSessions - excessTotal / SERIES_DAYS);
        return { date, value: round(baseline * (1 + (rand() - 0.5) * 0.3) + (excess[daysAgo] ?? 0), 2) };
      }
      case 'ANR_RATE': {
        const excess = ANR_EXCESS[p.id] ?? {};
        return { date, value: round(p.anrBaseline * (1 + (rand() - 0.5) * 0.3) + (excess[daysAgo] ?? 0), 2) };
      }
    }
  });
}

function aggregateSeries(metric: AnalyticsMetric): MetricPoint[] {
  const profiles = AGGREGATE_APP_IDS.map((id) => DEMO_PROFILES[id]);
  const perApp = profiles.map((p) => buildAppSeries(p, metric));
  const sessionSeries = profiles.map((p) => buildAppSeries(p, 'SESSIONS'));
  const summed = metric === 'ACTIVE_USERS' || metric === 'NEW_USERS' || metric === 'SESSIONS';

  return perApp[0].map((point, i) => {
    if (summed) {
      return { date: point.date, value: perApp.reduce((sum, series) => sum + (series[i].value ?? 0), 0) };
    }
    // Rates and durations: weight by each app's sessions that day.
    const weights = sessionSeries.map((s) => s[i].value ?? 0);
    const totalWeight = weights.reduce((a, b) => a + b, 0) || 1;
    const weighted = perApp.reduce((sum, series, k) => sum + (series[i].value ?? 0) * weights[k], 0) / totalWeight;
    return { date: point.date, value: metric === 'SESSION_DURATION' ? Math.round(weighted) : round(weighted, 2) };
  });
}

// ------------------------------------------------------------------ snapshot

interface ScaledNumbers {
  mau: number;
  newUsers: number;
  sessions: number;
  avgSessionSeconds: number;
  retentionRate: number;
  crashFreeSessions: number;
  trends: DemoAppProfile['trends'];
  from: number;
  to: number;
}

const PERIOD_FACTORS: Record<AnalyticsPeriod, { users: number; newUsers: number; sessions: number; trend: number }> = {
  THIS_MONTH: { users: 1, newUsers: 1, sessions: 1, trend: 1 },
  LAST_MONTH: { users: 1, newUsers: 1, sessions: 1, trend: 0.55 },
  LAST_3_MONTHS: { users: 1.85, newUsers: 2.7, sessions: 2.9, trend: 1.6 },
  THIS_YEAR: { users: 3.4, newUsers: 8.2, sessions: 8.5, trend: 2.6 },
};

/** How much better crash-free sessions were last month, for apps with a deteriorating story. */
const LAST_MONTH_CRASH_FREE_GAIN: Record<number, number> = { 8001: 0.45, 8003: 0.4 };

function scaleForPeriod(p: DemoAppProfile, period: AnalyticsPeriod): ScaledNumbers {
  const f = PERIOD_FACTORS[period];
  const now = Date.now();
  const t = p.trends;

  if (period === 'LAST_MONTH') {
    // The previous 30 days: the level the current trend was measured against.
    return {
      mau: p.mau / (1 + t.activeUsers / 100),
      newUsers: p.newUsers / (1 + t.newUsers / 100),
      sessions: p.sessions / (1 + t.sessions / 100),
      avgSessionSeconds: p.avgSessionSeconds / (1 + t.sessionDuration / 100),
      retentionRate: p.retentionRate - 1.2,
      // The Shopping crash spike and Delivery Partner's growth-related decline both landed in the
      // current window, so last month was cleaner for those two.
      crashFreeSessions: Math.min(99.9, p.crashFreeSessions + (LAST_MONTH_CRASH_FREE_GAIN[p.id] ?? 0.1)),
      trends: {
        activeUsers: round(t.activeUsers * f.trend, 1),
        newUsers: round(t.newUsers * f.trend, 1),
        sessions: round(t.sessions * f.trend, 1),
        sessionDuration: round(t.sessionDuration * f.trend, 1),
      },
      from: now - 60 * DAY_MS,
      to: now - 30 * DAY_MS,
    };
  }

  const days = period === 'THIS_MONTH' ? 30 : period === 'LAST_3_MONTHS' ? 90 : 273;
  return {
    mau: p.mau * f.users,
    newUsers: p.newUsers * f.newUsers,
    sessions: p.sessions * f.sessions,
    avgSessionSeconds: p.avgSessionSeconds * (period === 'THIS_MONTH' ? 1 : 0.985),
    retentionRate: p.retentionRate + (period === 'THIS_MONTH' ? 0 : period === 'LAST_3_MONTHS' ? -1.1 : -2.4),
    crashFreeSessions: Math.min(99.9, p.crashFreeSessions + (period === 'THIS_MONTH' ? 0 : 0.15)),
    trends: {
      activeUsers: round(t.activeUsers * f.trend, 1),
      newUsers: round(t.newUsers * f.trend, 1),
      sessions: round(t.sessions * f.trend, 1),
      sessionDuration: round(t.sessionDuration * f.trend, 1),
    },
    from: now - days * DAY_MS,
    to: now,
  };
}

function aggregateScaled(period: AnalyticsPeriod): ScaledNumbers {
  const parts = AGGREGATE_APP_IDS.map((id) => scaleForPeriod(DEMO_PROFILES[id], period));
  const sum = (pick: (s: ScaledNumbers) => number) => parts.reduce((a, s) => a + pick(s), 0);
  const weighted = (pick: (s: ScaledNumbers) => number, weight: (s: ScaledNumbers) => number) =>
    sum((s) => pick(s) * weight(s)) / sum(weight);

  return {
    mau: sum((s) => s.mau),
    newUsers: sum((s) => s.newUsers),
    sessions: sum((s) => s.sessions),
    avgSessionSeconds: weighted((s) => s.avgSessionSeconds, (s) => s.sessions),
    retentionRate: weighted((s) => s.retentionRate, (s) => s.mau),
    crashFreeSessions: weighted((s) => s.crashFreeSessions, (s) => s.sessions),
    trends: {
      activeUsers: round(weighted((s) => s.trends.activeUsers, (s) => s.mau), 1),
      newUsers: round(weighted((s) => s.trends.newUsers, (s) => s.newUsers), 1),
      sessions: round(weighted((s) => s.trends.sessions, (s) => s.sessions), 1),
      sessionDuration: round(weighted((s) => s.trends.sessionDuration, (s) => s.sessions), 1),
    },
    from: parts[0].from,
    to: parts[0].to,
  };
}

function profileFor(appId: string): DemoAppProfile | undefined {
  return DEMO_PROFILES[Number(appId)];
}

/** Fallback for apps created during the demo: a small, believable "just connected" app. */
function newAppProfile(appId: string): DemoAppProfile {
  return {
    ...DEMO_PROFILES[8002],
    id: Number(appId) || 0,
    mau: 1_840,
    newUsers: 620,
    sessions: 6_900,
    retentionRate: 41.5,
    trends: { activeUsers: 14.2, newUsers: 18.4, sessions: 11.7, sessionDuration: 1.9 },
  };
}

export function buildSnapshot(appId: string, period: AnalyticsPeriod): AppMetricsSnapshot {
  const isAll = appId === 'all' || !appId;
  const profile = isAll ? undefined : (profileFor(appId) ?? newAppProfile(appId));
  const s = profile ? scaleForPeriod(profile, period) : aggregateScaled(period);
  const syncedMinutesAgo = profile && profile.staleDays ? profile.staleDays * 24 * 60 : 12 + ((profile?.id ?? 0) % 7) * 6;

  return {
    scope: isAll ? 'ORGANIZATION' : 'APP',
    period,
    dataFrom: new Date(s.from).toISOString(),
    dataTo: new Date(s.to).toISOString(),
    activeUsers: Math.round(s.mau),
    newUsers: Math.round(s.newUsers),
    sessions: Math.round(s.sessions),
    avgSessionDurationSeconds: Math.round(s.avgSessionSeconds),
    retentionRate: round(s.retentionRate, 1),
    crashFreeSessions: round(s.crashFreeSessions, 1),
    trends: {
      activeUsersPct: s.trends.activeUsers,
      newUsersPct: s.trends.newUsers,
      sessionsPct: s.trends.sessions,
      sessionDurationPct: s.trends.sessionDuration,
    },
    lastSyncedAt: new Date(Date.now() - syncedMinutesAgo * 60 * 1000).toISOString(),
    insufficientData: false,
  };
}

// ------------------------------------------------------------------ time series

const UNIT: Record<AnalyticsMetric, string> = {
  ACTIVE_USERS: 'count',
  NEW_USERS: 'count',
  SESSIONS: 'count',
  SESSION_DURATION: 'seconds',
  CRASH_RATE: 'percent',
  ANR_RATE: 'percent',
};

export function buildTimeSeries(appId: string, metric: AnalyticsMetric): MetricTimeSeries {
  const isAll = appId === 'all' || !appId;
  const profile = isAll ? undefined : (profileFor(appId) ?? newAppProfile(appId));
  const points = profile ? buildAppSeries(profile, metric) : aggregateSeries(metric);
  return { metric, unit: UNIT[metric], points, insufficientData: false };
}

// ------------------------------------------------------------------ breakdowns

function appCountryRows(p: DemoAppProfile): MetricBreakdownRow[] {
  const rand = seededRandom(hashSeed(String(p.id), 'COUNTRY'));
  const totalWeight = p.countries.reduce((a, [, w]) => a + w, 0);
  return p.countries.map(([key, w]) => ({
    key,
    activeUsers: Math.round(p.mau * (w / totalWeight) * (1 + (rand() - 0.5) * 0.06)),
    // Engaged home markets run a little more sessions per user than the long tail.
    sessions: Math.round(p.sessions * (w / totalWeight) * (0.92 + rand() * 0.16)),
  }));
}

function appPlatformRows(p: DemoAppProfile): MetricBreakdownRow[] {
  const rows: MetricBreakdownRow[] = [];
  if (p.androidShare > 0) {
    rows.push({
      key: 'Android',
      activeUsers: Math.round(p.mau * p.androidShare),
      sessions: Math.round(p.sessions * p.androidShare * 0.94),
    });
  }
  if (p.androidShare < 1) {
    rows.push({
      key: 'iOS',
      activeUsers: Math.round(p.mau * (1 - p.androidShare)),
      // iOS users tend to run slightly more sessions per user.
      sessions: Math.round(p.sessions * (1 - p.androidShare) * (p.androidShare > 0 ? 1.08 : 1)),
    });
  }
  return rows;
}

function mergeRows(groups: MetricBreakdownRow[][]): MetricBreakdownRow[] {
  const merged = new Map<string, MetricBreakdownRow>();
  for (const row of groups.flat()) {
    const existing = merged.get(row.key);
    if (existing) {
      existing.activeUsers = (existing.activeUsers ?? 0) + (row.activeUsers ?? 0);
      existing.sessions = (existing.sessions ?? 0) + (row.sessions ?? 0);
    } else {
      merged.set(row.key, { ...row });
    }
  }
  return [...merged.values()];
}

function sortRows(rows: MetricBreakdownRow[]): MetricBreakdownRow[] {
  return rows.sort((a, b) => {
    if (a.key === 'Other') return 1;
    if (b.key === 'Other') return -1;
    return (b.activeUsers ?? 0) - (a.activeUsers ?? 0);
  });
}

export function buildBreakdown(appId: string, dimension: BreakdownDimension): MetricBreakdown {
  const isAll = appId === 'all' || !appId;
  const rowsFor = dimension === 'COUNTRY' ? appCountryRows : appPlatformRows;
  const rows = isAll
    ? mergeRows(AGGREGATE_APP_IDS.map((id) => rowsFor(DEMO_PROFILES[id])))
    : rowsFor(profileFor(appId) ?? newAppProfile(appId));

  return { dimension, rows: sortRows(rows), insufficientData: false };
}
